// ============================================================
// ORG MODEL — 3-tier holding company hierarchy
// ============================================================
// Real Fortune 100 customers don't have one account. They have:
//
//   Holding company (Org)
//     ├── Subsidiary / BU (Account)
//     │     ├── Site (Service line + Terminals)
//     │     └── Site …
//     └── Subsidiary / BU
//           └── Site …
//
// Examples:
//   ExxonMobil → Refining BU → Houston Plant #2
//   PG&E → Distribution → Substation #4218
//   Tallgrass → Pipeline Ops → KC compressor
//
// This module is the source of truth for that hierarchy in the
// SPA. Real backend stores the same shape in RDS — `orgs`,
// `org_membership`, `account.org_id` columns — and the Lambda
// resolves "what can this user see" by walking the membership
// graph.
//
// Pre-cutover: a single hardcoded Globaltrade Holdings org with
// three subsidiary accounts so the Org switcher + rollup UI
// behaves realistically in the demo. Drop this once the
// real backend is in flight.

import { getVertical } from '../data/mock/verticals.js';

// Each subsidiary is backed by a distinct vertical fleet (verticals.js) so
// the Org-switcher "Switch" lands each one on genuinely different data —
// LARGE Oil & Gas, MEDIUM mobile fleet, SMALL retail failover. Site counts
// and monthly cost are derived from the fleets so they never drift.
function subsidiary(accountNumber, bu) {
  const v = getVertical(accountNumber);
  return {
    accountNumber,
    name: v.account.accountName,
    bu,
    siteCount: v.stats.total,
    monthlyCostUsd: Math.round(v.stats.totalCostUsd),
    slaTier: v.account.slaTier,
    vertical: v.account.vertical,
  };
}

const HARDCODED_ORG = {
  orgId: 'ORG-GLOBALTRADE-001',
  name: 'Globaltrade Holdings, Inc.',
  shortName: 'Globaltrade',
  subsidiaries: [
    // LARGE — Oil & Gas. Default landing for owners; the "All" rollup
    // spans every subsidiary the user can access.
    subsidiary('ACC-DEMO-CTO-50FLEET-01', 'Energy — Upstream & Midstream'),
    // MEDIUM — logistics / mobile fleet mounted on vehicles.
    subsidiary('ACC-GLOBAL-FIELD-03', 'Logistics & Mobile Fleet'),
    // SMALL — retail storefronts using Starlink as failover.
    subsidiary('ACC-GLOBAL-RETAIL-02', 'Retail Operations'),
  ],
};

/**
 * Get the org this user belongs to. Real backend reads this from
 * the JWT's `org_id` claim and the org row in RDS. Today: returns
 * the hardcoded Globaltrade org IF the user is on a known demo
 * account number. Returns null otherwise (single-account customer
 * — no org rollup needed).
 */
export function getCurrentOrg(accountNumber) {
  if (!accountNumber) return null;
  // Match anyone in the Globaltrade subsidiary list, OR the
  // legacy ACC-CTO-50-FLEET alias.
  if (accountNumber === 'ACC-CTO-50-FLEET') return HARDCODED_ORG;
  if (HARDCODED_ORG.subsidiaries.some((s) => s.accountNumber === accountNumber)) return HARDCODED_ORG;
  return null;
}

/**
 * Roll up KPIs across every subsidiary the user can see. Real
 * backend does this with one query per subsidiary, in parallel,
 * then aggregates. Pre-cutover: synthesizes plausible totals.
 */
export function getOrgRollup(orgId) {
  const org = orgId === HARDCODED_ORG.orgId ? HARDCODED_ORG : null;
  if (!org) return null;
  const totals = org.subsidiaries.reduce(
    (acc, s) => {
      acc.siteCount += s.siteCount;
      acc.monthlyCostUsd += s.monthlyCostUsd;
      return acc;
    },
    { siteCount: 0, monthlyCostUsd: 0 },
  );
  return {
    org,
    totals,
    subsidiaryCount: org.subsidiaries.length,
  };
}

/**
 * List the subsidiaries the user can act-as. Drives the Org
 * Switcher dropdown in the TopBar. Real backend filters to the
 * subset the user has org-level admin permission on; pre-cutover
 * everyone gets all subsidiaries.
 */
export function listAccessibleSubsidiaries(orgId) {
  const org = orgId === HARDCODED_ORG.orgId ? HARDCODED_ORG : null;
  return org ? org.subsidiaries : [];
}
